import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 pt-16">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-emotion-sad-line text-foreground-400 text-2xl"></i>
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">分类未找到</h1>
          <p className="text-sm text-foreground-600 mb-6">抱歉，您访问的课程分类不存在</p>
          <Link to="/" className="whitespace-nowrap inline-block px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryArticles = articles.filter((a) => a.category === slug);

  return (
    <div className="min-h-screen bg-background-50 pt-16">
      <SchemaOrg
        data={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${category.name} - 女仆教育课程分类`,
          description: category.description,
          url: `https://hkxwhcm.cn/category/${category.slug}`,
        }}
      />
      <SchemaOrg
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://hkxwhcm.cn/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: '课程分类',
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: category.name,
            },
          ],
        }}
      />
      <div className="bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '课程分类', to: '/category/liyi-guifan' },
              { label: category.name },
            ]}
          />
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-primary-500 flex items-center justify-center">
              <i className={`${category.icon} text-background-50 text-xl md:text-2xl`}></i>
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {category.name}
              </h1>
              <p className="text-sm text-foreground-600 mt-1">
                {categoryArticles.length} 篇相关文章
              </p>
            </div>
          </div>
          <p className="text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
            {category.description}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                cat.slug === slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>

        {categoryArticles.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-list-3-line text-foreground-400 text-2xl"></i>
            </div>
            <p className="text-foreground-600 text-sm">该分类暂无文章</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {categoryArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
              >
                <div className="h-44 md:h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 md:p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs text-foreground-500">{article.date}</span>
                    <span className="text-xs text-foreground-500 flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {article.readTime} 阅读
                    </span>
                  </div>
                  <h2 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h2>
                  <p className="text-xs md:text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                    {article.excerpt}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}